/* eslint-disable react/destructuring-assignment */
import {
  fetchChildrenTagByRemoteId,
  fetchStartups,
  NB_STARTUP_PER_PAGE,
} from 'features/innovations/business/Startup.api';
import { InnovationsHomeLayout } from 'features/innovations/components/InnovationsHomeLayout/InnovationsHomeLayout.component';
import { InlineResponseStartup, InnovationTagEnum, Startup, Tag } from 'pleinchamp-api-client';
import React, { createContext, useEffect, useState } from 'react';
import { usePlcApiCall } from '@api/business/api.hook';
import { formatPropsForStatic } from '@utils/api.utils';
import { ExtendedPagination, extendPagination } from '@utils/pagination';
import { defaultNamespaces } from '@utils/strings';
import { PlcNextPage, PlcNextPageContext, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface PageProps {
  startupsContent: InlineResponseStartup;
  offset: number;
  pagination: ExtendedPagination;
  stakesFilterSelected: Tag[];
  domainsFilterSelected: Tag[];
  sectorsFilterSelected: Tag[];
  query: string;
  isHostedByCa: boolean;
  domainsList: Tag[];
  sectorsList: Tag[];
  stakesList: Tag[];
}

interface ContentProps {
  startups: Startup[];
  pagination: ExtendedPagination;
}

type ContextProps = PlcPageContextProps<Omit<PageProps, 'startupsContent'> & ContentProps> & {
  fetchNextStartups?: () => void;
};

export const InnovationsHomeContext = createContext<ContextProps>({
  domainsFilterSelected: { isValidating: false },
  domainsList: { isValidating: false },
  isHostedByCa: { isValidating: false },
  offset: { data: 0 },
  pagination: { isValidating: false },
  query: { isValidating: false },
  sectorsFilterSelected: { isValidating: false },
  sectorsList: { isValidating: false },
  stakesFilterSelected: { isValidating: false },
  stakesList: { isValidating: false },
  startups: { isValidating: false },
});

const InnovationsHome: PlcNextPage<Partial<PageProps>> = (props) => {
  const [query, setQuery] = useState<string>('');
  const [stakesFilterSelected, setStakesFilterSelected] = useState<Tag[]>([]);
  const [domainsFilterSelected, setDomainsFilterSelected] = useState<Tag[]>([]);
  const [sectorsFilterSelected, setSectorsFilterSelected] = useState<Tag[]>([]);
  const [hostedByCa, setHostedByCa] = useState<boolean>(false);
  const [offset, setOffset] = useState(0);
  const [startups, setStartups] = useState(props.startupsContent ? (props.startupsContent.contents as Startup[]) : []);
  const [pagination, setPagination] = useState(
    props.startupsContent ? extendPagination(props.startupsContent.pagination) : undefined
  );

  // Recall with dedumping
  const { data, initialData, ...rest } = usePlcApiCall(
    fetchStartups,
    [query, stakesFilterSelected, domainsFilterSelected, sectorsFilterSelected, hostedByCa, offset],
    {
      initialData: props.startupsContent,
    }
  );

  const domainsList = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.domain, 1], {
    initialData: props.domainsList,
  });
  const sectorsList = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.sector], {
    initialData: props.sectorsList,
  });
  const stakesList = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.stake], {
    initialData: props.stakesList,
  });

  useEffect(() => {
    if (data) {
      const { pagination: nextPagination, contents } = data;
      setStartups(startups.slice(0, nextPagination.offset).concat(contents as Startup[]));
      setPagination(extendPagination(nextPagination));
    }
  }, [data]);

  // Handle the 'Load more' startups
  function fetchNextStartups() {
    if (pagination?.hasNext) {
      setOffset(offset + NB_STARTUP_PER_PAGE);
    }
  }

  return (
    <InnovationsHomeContext.Provider
      value={{
        domainsFilterSelected: { data: domainsFilterSelected, isValidating: false, setData: setDomainsFilterSelected },
        domainsList: { data: domainsList.data, isValidating: false },
        fetchNextStartups,
        isHostedByCa: { data: hostedByCa, setData: setHostedByCa },
        offset: { data: offset, setData: setOffset },
        pagination: { data: pagination, isValidating: false },
        query: { data: query, setData: setQuery },
        sectorsFilterSelected: { data: sectorsFilterSelected, isValidating: false, setData: setSectorsFilterSelected },
        sectorsList: { data: sectorsList.data, isValidating: false },
        stakesFilterSelected: { data: stakesFilterSelected, isValidating: false, setData: setStakesFilterSelected },
        stakesList: { data: stakesList.data, isValidating: false },
        startups: { ...rest, data: startups },
      }}
    >
      <InnovationsHomeLayout />
    </InnovationsHomeContext.Provider>
  );
};

export async function getStaticProps({ locale }: PlcNextPageContext) {
  const props = await Promise.all([
    fetchStartups(),
    fetchChildrenTagByRemoteId(InnovationTagEnum.domain, 1),
    fetchChildrenTagByRemoteId(InnovationTagEnum.stake),
    fetchChildrenTagByRemoteId(InnovationTagEnum.sector),
    await serverSideTranslations(locale || 'fr', [...defaultNamespaces, 'common', 'innovations']),
  ])
    .then(([startupContent, domains, stakes, sectors, i18n]) => ({
      ...i18n,
      domains,
      sectors,
      stakes,
      startupContent,
    }))
    .catch(async (err) => {
      console.error('les-innovations', err);
      return serverSideTranslations(locale || 'fr', [...defaultNamespaces, 'common', 'innovations']);
    });

  return formatPropsForStatic(props);
}

export default InnovationsHome;
